/**
 * L.6.2: subscribe to `session.tool` events and honour agent-initiated
 * file-open signals.
 *
 * The plugin's `lx_open_file` tool doesn't do any server-side I/O —
 * its sole purpose is to ride the gateway's automatic `session.tool`
 * broadcast (server-chat.ts emits one per tool invocation). On the
 * client, this hook picks the signal up and calls
 * `useWorkspaceFileStore.openFile({ path })`. The viewer then
 * dispatches by extension exactly as if the user had clicked the
 * file themselves.
 *
 * ## Payload shape (L.6.2 revision)
 *
 * The gateway's real `session.tool` payload is the `AgentEventPayload`
 * shape from `lexruntime/openclaw/src/infra/agent-events.ts`:
 *   `{runId, seq, stream, ts, sessionKey?, data: {phase, toolName, toolInput, ...}}`
 * Tool fields live under `data.*` — NOT at the top level. An earlier
 * draft of this hook read `payload.toolName`, which matched a stale
 * forward-declared `AgentEventPayload` type in `lexide/src/types/gateway.ts`
 * but not the actual server output.
 *
 * ## Phase filter
 *
 * The gateway emits multiple `session.tool` events per invocation
 * (typically `phase: "start"` then `phase: "end"`). We fire only on
 * `start` — that's the earliest event that carries `toolInput` with
 * the requested path, and it lets the pane open while the tool is
 * still "pending" on the agent side. Other phases are no-ops even
 * though `openFile` would be idempotent on identical paths.
 *
 * ## Scope gate (added 2026-08-20 — instance 2 of the cross-case read family)
 *
 * 🚨 FAIL-CLOSED on the event's `sessionKey`, same rule and same
 * asymmetry as `askBelongsToSession` (whose predicate this reuses): the
 * gateway fans `session.tool` out to EVERY connection of the user, so an
 * un-gated open fanned one case's relative path into every open window,
 * each resolving it against ITS OWN case — measured live 2026-08-20
 * (4 connections on 2 other cases reading a nadir-case record, NOT_FOUND
 * ×8; bus thread `…-j3nw`, instance 2). A missed auto-open is covered by
 * the agent's own reply text; a foreign-case open is a wrong-case read
 * plus a viewer showing an error for a file that "should" exist.
 *
 * `useWorkspaceFileStore` stays layout-agnostic; if the agent fires
 * `lx_open_file` from a non-case scope the plugin registration rejects
 * it (`tools/index.ts`), and this gate additionally drops anything not
 * addressed to THIS window's conversation.
 *
 * See: ai-docs/lexide-layout-plan.md §10 Phase L.6.2
 */

import { useEventStream } from "@/hooks/useEventStream";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";
import { askBelongsToSession } from "@/lib/ask-user";

/** Shape mirror of the real `AgentEventPayload` emitted by
 *  `server-chat.ts`. Kept local so this hook doesn't depend on the
 *  also-stale declaration in `types/gateway.ts`.
 *
 *  ⚠ Both spellings are declared because BOTH have been believed at different
 *  times — see the field-naming note on the hook below for which one the wire
 *  actually sends. */
interface AgentEventPayloadRuntime {
  runId?: string;
  seq?: number;
  stream?: string;
  ts?: number;
  sessionKey?: string;
  data?: {
    phase?: string;
    name?: string;
    args?: Record<string, unknown>;
    toolName?: string;
    toolInput?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

/**
 * ⚖ **FIXED 2026-08-13 — this hook had never fired.**
 *
 * It matched on `data.toolName`, and the wire only ever sends `data.name`
 * (`pi-embedded-subscribe.handlers.tools.ts:651-660` — pi-native `name`/`args`;
 * the `toolName`/`toolInput` spelling belongs to a rekeyed variant that this
 * path does not produce). The comparison could never be true, so
 * agent-initiated `lx_open_file` was inert for its entire life while looking
 * completely correct in review — no error, no warning, just a pane that never
 * opened.
 *
 * Found by the `session.tool`-consumer audit in
 * `ai-docs/lexide-concierge-live-activity-l0-l1-miniplan.md` §1. Both spellings
 * are now accepted: the cost is one `??` and the alternative failure mode is
 * silence.
 */
export function useFileOpenFromAgent(currentSessionKey: string | undefined): void {
  const openFile = useWorkspaceFileStore((s) => s.openFile);

  useEventStream("session.tool", (payload) => {
    const typed = payload as AgentEventPayloadRuntime;
    // Only the start-phase event carries the args; fire once per
    // invocation to avoid redundant store writes.
    if (typed.data?.phase !== "start") return;
    const name = typed.data.name ?? typed.data.toolName;
    if (name !== "lx_open_file") return;
    // Scope gate — see docblock. ONE definition of the rule, shared with
    // the ask surface: no sessionKey (either side) ⇒ not ours, drop.
    if (!askBelongsToSession({ sessionKey: typed.sessionKey }, currentSessionKey)) return;
    const path = (typed.data.args ?? typed.data.toolInput)?.path;
    if (typeof path !== "string" || path.length === 0) return;
    openFile({ path });
  });
}
